import type { CompatibilityInput, CompatibilityAnalysis } from '../types/compatibility';
import { analyzeSaju } from '../engine';
import { analyzeCrossRelations } from './cross-relations';
import { analyzeOhaengHarmony } from './ohaeng-harmony';
import { analyzeYongSinCompatibility } from './yongsin-compatibility';
import { analyzeGuiinMatching } from './guiin-matching';
import { analyzeNapeumCompat } from './napeum-compat';
import { analyzeZodiacCompat } from './zodiac-compat';
import { analyzeSpousePalace } from './spouse-palace';
import { analyzeFortuneFlow } from './fortune-flow';
import { analyzeStarCross } from './star-cross';
import { analyzeChildrenFortune } from './children-fortune';
import { analyzeJohooCompat } from './johoo-compat';
import { analyzeStrengthCross } from './strength-cross';
import { analyzeGyeokGukCompat } from './gyeokguk-compat';
import { analyzeTwelveStageCross } from './twelve-stage-cross';
import { analyzeMarriagePotential } from './marriage-potential';
import { analyzeSpouseRole } from './spouse-role';
import { analyzeSangsaengji } from './sangsaengji';
import { determineGrade, generateSummary } from './scoring';

/**
 * 두 사람의 사주 궁합을 종합 분석한다.
 *
 * 16개 분석 모듈 + 5차원 채점 + 강렬도 + 혼인 그릇 + 전통 체크리스트
 *
 * compatMode:
 * - 'standard': 기존 하이브리드 엔진 (기본값)
 * - 'traditional': 자평 코어 중시 (격국/용신 위계 캡, 신살 비중↓)
 */
export function analyzeCompatibility(input: CompatibilityInput): CompatibilityAnalysis {
  const type = input.type ?? 'romantic';
  const compatMode = input.compatMode ?? 'standard';
  const analysis1 = analyzeSaju(input.person1, input.options);
  const analysis2 = analyzeSaju(input.person2, input.options);
  const noHour = input.options?.noHour === true
    || input.person1.hour === undefined
    || input.person2.hour === undefined;

  const crossRelations = analyzeCrossRelations(analysis1.fourPillars, analysis2.fourPillars, noHour);
  const ohaengHarmony = analyzeOhaengHarmony(analysis1, analysis2);
  const yongSinCompat = analyzeYongSinCompatibility(analysis1, analysis2);
  const guiinMatches = analyzeGuiinMatching(analysis1.fourPillars, analysis2.fourPillars, noHour);
  const napeumCompat = analyzeNapeumCompat(analysis1.fourPillars, analysis2.fourPillars);
  const zodiacCompat = analyzeZodiacCompat(analysis1.fourPillars, analysis2.fourPillars);
  const spousePalace = analyzeSpousePalace(analysis1, analysis2);
  const fortuneFlow = analyzeFortuneFlow(analysis1, analysis2);
  const starCross = analyzeStarCross(analysis1, analysis2, noHour);
  const childrenFortune = analyzeChildrenFortune(analysis1, analysis2);
  const johooCompat = analyzeJohooCompat(analysis1, analysis2);
  const strengthCross = analyzeStrengthCross(analysis1, analysis2);
  const gyeokGukCompat = analyzeGyeokGukCompat(analysis1, analysis2);
  const twelveStageCross = analyzeTwelveStageCross(analysis1, analysis2);
  const spouseRole = analyzeSpouseRole(analysis1, analysis2);
  const sangsaengji = analyzeSangsaengji(analysis1, analysis2);

  // 혼인 그릇 (romantic/family에서만)
  const marriagePotential = (type === 'romantic' || type === 'family')
    ? { person1: analyzeMarriagePotential(analysis1), person2: analyzeMarriagePotential(analysis2) }
    : undefined;

  const gradeResult = determineGrade({
    crossRelations, ohaengHarmony, yongSinCompat, guiinMatches,
    napeumCompat, zodiacCompat, spousePalace, fortuneFlow, starCross, childrenFortune,
    johooCompat, strengthCross, gyeokGukCompat, twelveStageCross,
    spouseRole, sangsaengji, compatMode, marriagePotential,
    type, analysis1, analysis2,
  });
  const summary = generateSummary(gradeResult, type);

  return {
    person1: analysis1,
    person2: analysis2,
    type,
    gradeResult,
    crossRelations,
    ohaengHarmony,
    yongSinCompatibility: yongSinCompat,
    guiinMatches,
    napeumCompat,
    zodiacCompat,
    spousePalace,
    fortuneFlow,
    starCross,
    childrenFortune,
    johooCompat,
    strengthCross,
    gyeokGukCompat,
    twelveStageCross,
    spouseRole,
    sangsaengji,
    marriagePotential,
    summary,
  };
}
